import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { TierBadge, ScoreChip } from "@/components/TierBadge";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  MessageCircle, Phone, Mail, Sparkles, Copy, Check, AlertTriangle,
  Trophy, ChevronRight, Loader2, UserCheck, BookOpen,
  Gauge, Target, Compass, CheckCircle2, ThumbsUp,
  Download, RefreshCw, Quote, ShieldAlert,
  User, StickyNote, Paperclip, Send, Trash2, FileText, Save,
  Globe, Linkedin, FileDown, Wand2, Pencil,
} from "lucide-react";

const STAGES = ["New Lead", "Connection Sent", "Connected", "Pitched", "future", "Details Sent", "Price Discussed", "Negotiation", "Won", "Lost"];
const OBJECTIONS = ["No Paid PR", "No Budget", "Low Traffic", "No ROI"];
const FULFILL = [
  ["questionnaire_filled", "Questionnaire filled"],
  ["story_drafted", "Story drafted"],
  ["sent_for_approval", "Sent for approval"],
  ["approved", "Story approved"],
  ["published", "Story published"],
];
const PUBS = [["startup_times", "Startup Times"], ["inc91", "INC91"], ["business_press", "Business Press"], ["gulf_times", "Gulf Times"]];

function normalizeWebsiteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (!value) return "";

  // Already has a protocol
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  // Protocol missing — add HTTPS
  return `https://${value}`;
}

async function safeCopy(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text || "");
      return true;
    }
    throw new Error("no clipboard api");
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text || "";
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

function CopyBtn({ text, tid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={tid}
      onClick={() => { safeCopy(text || ""); setDone(true); setTimeout(() => setDone(false), 1200); }}
      className="text-zinc-500 hover:text-klein transition-colors"
    >
      {done ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

// LinkedIn URL field: shows the saved URL with copy + edit, or an input to add one.
function LinkedInField({ lead, patch }) {
  const current = lead.linkedin_url || "";
  const [editing, setEditing] = useState(!current);
  const [val, setVal] = useState(current);

  useEffect(() => { setVal(current); setEditing(!current); }, [current, lead.id]);

  const save = async () => {
    const v = val.trim();
    if (!v) return;
    if (!/linkedin\.com\//i.test(v)) { toast.error("Valid LinkedIn URL daalo"); return; }
    await patch({ linkedin_url: normalizeWebsiteUrl(v) });
    setEditing(false);
    toast.success("LinkedIn URL saved");
  };

  return (
    <div data-testid="linkedin-field">
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">LinkedIn URL</label>

      {!editing && current ? (
        <div className="flex items-center gap-1.5 border border-zinc-400 bg-white px-2 py-2">
          <Linkedin className="h-3.5 w-3.5 text-klein shrink-0" />
          <a href={current} target="_blank" rel="noreferrer" title={current} data-testid="linkedin-link"
             className="flex-1 min-w-0 truncate text-xs text-klein hover:underline">
            {current.replace(/^https?:\/\/(www\.)?/i, "")}
          </a>
          <CopyBtn text={current} tid="copy-linkedin" />
          <button data-testid="linkedin-edit" onClick={() => setEditing(true)} className="text-zinc-500 hover:text-klein transition-colors">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <input data-testid="linkedin-input" value={val} onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            placeholder="linkedin.com/in/username"
            className="flex-1 min-w-0 border border-zinc-400 bg-white text-xs px-2 py-2 focus:border-klein focus:outline-none" />
          <button data-testid="linkedin-save" onClick={save} disabled={!val.trim()}
            className="bg-klein hover:bg-klein-hover text-white px-2.5 py-2 disabled:opacity-50 transition-colors">
            <Check className="h-3.5 w-3.5" />
          </button>
          {current && (
            <button onClick={() => { setVal(current); setEditing(false); }}
              className="text-xs border border-zinc-300 px-2 py-2 hover:bg-zinc-100">✕</button>
          )}
        </div>
      )}
    </div>
  );
}


export default function LeadDrawer({ leadId, open, onClose, onChanged }) {
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [tab, setTab] = useState("signals");
  const [dealValue, setDealValue] = useState(9999);
  const [pkg, setPkg] = useState("Standard");
  const [reps, setReps] = useState([]);
  const [playbook, setPlaybook] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [dossierBusy, setDossierBusy] = useState(false);

  const load = () => {
    if (!leadId) return;
    api.get(`/leads/${leadId}`).then((r) => { setLead(r.data); setMsgs(null); setPlaybook(null); setBrief(null); setTab(r.data.stage === "Won" ? "fulfillment" : "brief"); });
  };
  useEffect(() => { if (open && leadId) load(); }, [open, leadId]);

  useEffect(() => {
    if (tab === "brief" && lead && !brief && !briefBusy) {
      setBriefBusy(true);
      api.post(`/leads/${lead.id}/brief`).then((r) => setBrief(r.data)).catch(() => {}).finally(() => setBriefBusy(false));
    }
  }, [tab, lead, brief, briefBusy]);

  useEffect(() => {
    if (open && user.role === "admin" && reps.length === 0) {
      api.get("/auth/users").then((r) => setReps(r.data)).catch(() => {});
    }
  }, [open, user, reps.length]);

  useEffect(() => {
    if (tab === "objections" && lead?.objections?.length && !playbook) {
      const latest = lead.objections[lead.objections.length - 1];
      api.get("/objections/playbook", { params: { type: latest } }).then((r) => setPlaybook(r.data)).catch(() => {});
    }
  }, [tab, lead, playbook]);

  if (!open || !lead) return null;

  const canBDE = user.role === "admin" || user.role === "bde";
  const digits = (lead.phone || "").replace(/\D/g, "");
  const websiteUrl = normalizeWebsiteUrl(lead.website);

  const patch = async (payload) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, payload);
      setLead(data); onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/messages`);
      setMsgs(data);
      toast.success("Messages generated");
    } catch (e) { toast.error(errText(e)); } finally { setGenBusy(false); }
  };

  const logObjection = async (ot) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/objections`, { objection_type: ot });
      if (data.playbook) setPlaybook(data.playbook);
      toast.success(`Logged objection: ${ot}`);
      setLead((l) => ({ ...l, objections: data.objections }));
      onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };

  const assign = async (uid) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/assign`, { user_id: uid || null });
      setLead(data); onChanged?.();
      toast.success(uid ? "Lead reassigned" : "Lead unassigned");
    } catch (e) { toast.error(errText(e)); }
  };

  const markWon = async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/win`, { package: pkg, deal_value: Number(dealValue) });
      toast.success("Marked Won — ICP recalculated");
      if (data.icp_changelog) toast.message(data.icp_changelog);
      setLead(data.lead); setTab("fulfillment"); onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };

  const setFulfill = async (payload) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}/fulfillment`, payload);
      setLead(data); onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };

  const downloadDossier = async () => {
    setDossierBusy(true);
    try {
      const res = await api.get(`/leads/${lead.id}/dossier`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${lead.name.replace(/\s+/g, "-").toLowerCase()}-dossier.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dossier PDF downloaded");
    } catch (e) { toast.error(errText(e)); } finally { setDossierBusy(false); }
  };

  const waText = encodeURIComponent(msgs?.whatsapp_text || lead.connection_note || `Hi ${lead.name.split(" ")[0]}, this is the team at Startup Times.`);
  const mailBody = encodeURIComponent(msgs?.email_body || "");
  const mailSubj = encodeURIComponent(msgs?.email_subject || `Featuring ${lead.company}`);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0 rounded-none border-l border-zinc-200 elev-2xl" data-testid="lead-drawer">
        {/* Header */}
        <div className="bg-[#09090B] text-white p-5">
          <SheetHeader>
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-white font-head text-2xl tracking-tight">{lead.name}</SheetTitle>
                <p className="text-zinc-400 text-sm mt-0.5">{lead.self_described_title || lead.formal_title} · {lead.company}</p>
                <p className="text-zinc-500 text-xs font-mono mt-1">{lead.location} · {(lead.follower_count || 0).toLocaleString()} followers</p>
                {lead.pseudo_profile && (
                  <span data-testid="lead-pseudo-profile" className="inline-flex items-center gap-1 mt-2 text-[10px] font-mono uppercase tracking-wide bg-purple-500/20 text-purple-200 border border-purple-400/40 rounded-md px-2 py-0.5">
                    Generated via {lead.pseudo_profile}
                  </span>
                )}
              </div>
              <div className="text-right mt-4">
                <ScoreChip score={lead.fit_score} />
                <div className=""><TierBadge tier={lead.tier} /></div>
                <div className="flex mt-1 gap-1 items-center justify-center" >
                <p className="">{digits}</p>
                <CopyBtn text={digits} tid="copy-phone" />
                </div>
              </div>
            </div>
          </SheetHeader>

          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            <a data-testid="wa-link" href={digits ? `https://wa.me/${digits}?text=${waText}` : "#"} target="_blank" rel="noreferrer"
               className="flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium py-2 transition-colors">
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
            <a data-testid="call-link" href={lead.phone ? `tel:${lead.phone}` : "#"}
               className="flex items-center justify-center gap-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-medium py-2 transition-colors">
              <Phone className="h-3.5 w-3.5" /> Call
            </a>
            <a data-testid="email-link" href={lead.email ? `mailto:${lead.email}?subject=${mailSubj}&body=${mailBody}` : "#"}
               className="flex items-center justify-center gap-1.5 bg-klein hover:bg-klein-hover text-white text-xs font-medium py-2 transition-colors">
              <Mail className="h-3.5 w-3.5" /> Email
            </a>
          </div>

          {/* Overview: prominent links + dossier */}
          <div className="grid grid-cols-3 gap-2 mt-2">
            <a data-testid="overview-linkedin" href={lead.linkedin_url || "#"} target="_blank" rel="noreferrer"
               onClick={(e) => { if (!lead.linkedin_url) e.preventDefault(); }}
               className={`flex items-center justify-center gap-1.5 text-xs font-medium py-2 border transition-colors ${lead.linkedin_url ? "border-zinc-600 text-zinc-200 hover:bg-white/10" : "border-zinc-800 text-zinc-600 cursor-not-allowed"}`}>
              <Linkedin className="h-3.5 w-3.5" /> LinkedIn
            </a>
            <a data-testid="overview-website" href={websiteUrl || "#"} target="_blank" rel="noreferrer"
               onClick={(e) => { if (!websiteUrl) e.preventDefault(); }}
               className={`flex items-center justify-center gap-1.5 text-xs font-medium py-2 border transition-colors ${lead.website ? "border-zinc-600 text-zinc-200 hover:bg-white/10" : "border-zinc-800 text-zinc-600 cursor-not-allowed"}`}>
              <Globe className="h-3.5 w-3.5" /> Website
            </a>
            <button data-testid="download-dossier" onClick={downloadDossier} disabled={dossierBusy}
               className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 border border-gold text-gold hover:bg-gold/10 transition-colors disabled:opacity-50">
              {dossierBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Dossier
            </button>
          </div>
        </div>

        {/* Next action */}
        {lead.next_action && (
          <div className="bg-gold px-5 py-3 border-y-2 border-black">
            <div className="text-[10px] font-bold uppercase tracking-widest text-black/60">Next best action</div>
            <div className="font-medium text-sm text-black flex items-center gap-1.5">
              <ChevronRight className="h-4 w-4" /> {lead.next_action}
            </div>
          </div>
        )}

        {/* Exclusion flags */}
        {lead.exclusion_flags?.length > 0 && (
          <div className="mx-5 mt-4 border border-amber-400 bg-amber-50 p-3">
            <div className="flex items-center gap-1.5 text-amber-800 text-xs font-bold uppercase tracking-wide">
              <AlertTriangle className="h-3.5 w-3.5" /> Warnings (not blocking)
            </div>
            <ul className="mt-1 text-xs text-amber-900 list-disc pl-5 space-y-0.5">
              {lead.exclusion_flags.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}

        {/* Stage + LinkedIn URL */}
        <div className="px-5 mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Stage</label>
            <select data-testid="stage-select" value={lead.stage} onChange={(e) => patch({ stage: e.target.value })}
              className="w-full border border-zinc-400 bg-white text-sm px-2 py-2 focus:border-klein focus:outline-none">
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <LinkedInField lead={lead} patch={patch} />
        </div>

        {/* Assignment */}
        <div className="px-5 mt-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Assigned rep</label>
          {user.role === "admin" ? (
            <select data-testid="assign-select" value={lead.assigned_to || ""} onChange={(e) => assign(e.target.value)}
              className="w-full border border-zinc-400 bg-white text-sm px-2 py-2 focus:border-klein focus:outline-none">
              <option value="">— unassigned —</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.role})</option>)}
            </select>
          ) : (
            <div className="flex items-center gap-2 text-sm border border-zinc-300 bg-zinc-50 px-3 py-2" data-testid="assigned-chip">
              <UserCheck className="h-4 w-4 text-zinc-500" />
              {lead.assigned_to_name || "Unassigned"}
            </div>
          )}
        </div>

        {/* Follow-up reminder */}
        <div className="px-5 mt-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Follow-up reminder</label>
          <div className="flex items-center gap-1.5 mb-1.5">
            {[["Tomorrow", 1], ["In 3 days", 3], ["Next week", 7]].map(([label, n]) => (
              <button key={n} data-testid={`preset-${n}`} onClick={() => {
                const d = new Date(); d.setDate(d.getDate() + n);
                patch({ follow_up_date: d.toISOString().slice(0, 10) });
              }}
                className="text-[11px] font-medium border border-zinc-300 rounded-md px-2 py-1 hover:border-klein hover:text-klein transition-colors">
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="date" data-testid="followup-date" value={(lead.follow_up_date || "").slice(0, 10)}
              onChange={(e) => {
                const t = (lead.follow_up_date || "").slice(11, 16);
                patch({ follow_up_date: e.target.value ? (t ? `${e.target.value}T${t}` : e.target.value) : null });
              }}
              className="flex-1 border border-zinc-300 rounded-lg bg-white text-sm px-2.5 py-2 focus:border-klein focus:ring-2 focus:ring-klein/20 focus:outline-none" />
            <input type="time" data-testid="followup-time" value={(lead.follow_up_date || "").slice(11, 16)}
              onChange={(e) => {
                const dpart = (lead.follow_up_date || "").slice(0, 10);
                if (!dpart) { toast.error("Pick a date first"); return; }
                patch({ follow_up_date: e.target.value ? `${dpart}T${e.target.value}` : dpart });
              }}
              title="Optional time"
              className="w-28 border border-zinc-300 rounded-lg bg-white text-sm px-2.5 py-2 focus:border-klein focus:ring-2 focus:ring-klein/20 focus:outline-none" />
            {lead.follow_up_date && (
              <button data-testid="followup-clear" onClick={() => patch({ follow_up_date: null })}
                className="text-xs border border-zinc-300 rounded-lg px-2.5 py-2 hover:bg-zinc-100 transition-colors">Clear</button>
            )}
          </div>
          <p className="text-[10px] text-zinc-400 mt-1">Time is optional. Added {(lead.created_date || "").slice(0, 10) || "—"}.</p>
        </div>

        <div className="mx-5 mt-5 flex gap-1 bg-zinc-100 rounded-xl p-1 overflow-x-auto kanban-scroll">
          {["brief", "profile", "signals", "messages", "compose", "notes", "files", "objections", ...(lead.stage === "Won" ? ["fulfillment"] : [])].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-lg shrink-0 whitespace-nowrap transition-all ${tab === t ? "bg-white text-klein shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "brief" && (
            <div data-testid="brief-panel">
              {briefBusy && !brief && (
                <div className="flex items-center gap-2 text-sm text-zinc-500 py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Building intelligence brief…
                </div>
              )}
              {brief && <BriefView brief={brief} leadId={lead.id} leadName={lead.name} senderDefault={user.name} />}
            </div>
          )}

          {tab === "profile" && <ProfileTab lead={lead} onSaved={(d) => { setLead(d); onChanged?.(); }} canEdit={user.role === "admin" || user.role === "bde" || user.role === "leadgen"} />}

          {tab === "notes" && <NotesTab leadId={lead.id} />}

          {tab === "compose" && <ComposeTab leadId={lead.id} />}

          {tab === "files" && <FilesTab leadId={lead.id} />}

          {tab === "signals" && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500 mb-2">Why this lead scored {lead.fit_score}/100 — plain-language breakdown:</p>
              {lead.score_signals?.map((s, i) => (
                <div key={i} className={`flex items-start justify-between border p-2.5 ${s.positive ? "border-emerald-200 bg-emerald-50" : "border-zinc-200 bg-white"}`}>
                  <div>
                    <div className="text-sm font-medium">{s.label}</div>
                    <div className="text-xs text-zinc-500">{s.detail}</div>
                  </div>
                  <span className={`font-mono text-sm font-bold ${s.positive ? "text-emerald-700" : "text-zinc-300"}`}>+{s.points}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "messages" && (
            <div className="space-y-4">
              <button data-testid="generate-messages" onClick={generate} disabled={genBusy}
                className="w-full bg-klein hover:bg-klein-hover text-white text-sm font-medium py-2.5 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                {genBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {msgs ? "Regenerate messages" : "Generate pitch & messages"}
              </button>
              {msgs && (
                <div className="space-y-4">
                  <MsgBlock title="Connection note" text={msgs.connection_note} tid="msg-connection" />
                  <MsgBlock title="First pitch" text={msgs.first_pitch} tid="msg-pitch" />
                  {msgs.mock_headline && <MsgBlock title="Mock headline preview" text={msgs.mock_headline} tid="msg-headline" />}
                  {msgs.follow_ups?.map((f, i) => (
                    <MsgBlock key={i} title={`Follow-up · ${f.label}`} text={f.text} tid={`msg-follow-${i}`} />
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "objections" && (
            <div>
              <p className="text-xs text-zinc-500 mb-3">Quick-tag the objection when a lead pushes back:</p>
              <div className="grid grid-cols-2 gap-2">
                {OBJECTIONS.map((o) => (
                  <button key={o} data-testid={`objection-${o.replace(/\s/g, "-").toLowerCase()}`} onClick={() => logObjection(o)}
                    className={`border px-3 py-2.5 text-sm font-medium transition-colors ${lead.objections?.includes(o) ? "bg-red-600 text-white border-red-700" : "bg-white border-zinc-400 hover:bg-red-50"}`}>
                    {o}
                  </button>
                ))}
              </div>
              {lead.objections?.length > 0 && (
                <p className="mt-3 text-xs font-mono text-zinc-500">Logged: {lead.objections.join(", ")}</p>
              )}
              {playbook && playbook.script && (
                <div className="mt-4 border-2 border-black bg-white rise" data-testid="objection-playbook">
                  <div className="flex items-center justify-between px-3 py-2 bg-gold border-b-2 border-black">
                    <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                      <BookOpen className="h-3.5 w-3.5" /> Best-recovering script · {playbook.objection_type}
                    </span>
                    <CopyBtn text={playbook.script.body_text} tid="copy-playbook" />
                  </div>
                  <div className="p-3">
                    <p className="text-sm leading-relaxed">{playbook.script.body_text}</p>
                    <div className="mt-2 flex items-center gap-3 text-[10px] font-mono text-zinc-500">
                      <span>v{playbook.script.version}</span>
                      <span className={playbook.recovery_rate >= 40 ? "text-emerald-700 font-bold" : ""}>
                        {playbook.recovery_rate}% recover after "{playbook.objection_type}"
                      </span>
                      <span>· based on {playbook.based_on_n} leads</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "fulfillment" && lead.stage === "Won" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-700"><Trophy className="h-4 w-4" /><span className="font-head font-bold">Fulfillment checklist</span></div>
              {FULFILL.map(([k, label]) => (
                <label key={k} data-testid={`fulfill-${k}`} className="flex items-center gap-3 border border-zinc-300 bg-white px-3 py-2.5 cursor-pointer hover:bg-zinc-50">
                  <input type="checkbox" checked={!!lead.fulfillment?.[k]} onChange={(e) => setFulfill({ [k]: e.target.checked })} className="h-4 w-4 accent-klein" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
              <div className="pt-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Publication links</div>
                {PUBS.map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs w-28 shrink-0 text-zinc-600">{label}</span>
                    <input data-testid={`pub-${k}`} defaultValue={lead.fulfillment?.publication_links?.[k] || ""}
                      onBlur={(e) => setFulfill({ publication_links: { [k]: e.target.value } })}
                      placeholder="https://…"
                      className="flex-1 border border-zinc-300 text-xs px-2 py-1.5 focus:border-klein focus:outline-none font-mono" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Package</div>
                  <select value={lead.package || "Standard"} onChange={(e) => setFulfill({ package: e.target.value })}
                    className="w-full border border-zinc-400 text-sm px-2 py-2">
                    <option>Standard</option><option>Advance</option>
                  </select>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Deal value (₹)</div>
                  <input type="number" defaultValue={lead.deal_value || 0} onBlur={(e) => setFulfill({ deal_value: Number(e.target.value) })}
                    className="w-full border border-zinc-400 text-sm px-2 py-2 font-mono" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mark won */}
        {canBDE && lead.stage !== "Won" && lead.stage !== "Lost" && (
          <div className="px-5 pb-8">
            <div className="border-2 border-black p-4 bg-white">
              <div className="text-xs font-bold uppercase tracking-wider mb-2">Close deal</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select data-testid="win-package" value={pkg} onChange={(e) => { setPkg(e.target.value); setDealValue(e.target.value === "Advance" ? 24999 : 9999); }}
                  className="border border-zinc-400 text-sm px-2 py-2">
                  <option>Standard</option><option>Advance</option>
                </select>
                <input data-testid="win-value" type="number" value={dealValue} onChange={(e) => setDealValue(e.target.value)}
                  className="border border-zinc-400 text-sm px-2 py-2 font-mono" />
              </div>
              <button data-testid="mark-won" onClick={markWon}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 flex items-center justify-center gap-2 transition-colors hard-shadow-sm">
                <Trophy className="h-4 w-4" /> MARK WON &amp; RETRAIN ICP
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function MsgBlock({ title, text, tid }) {
  const [val, setVal] = useState(text);
  useEffect(() => { setVal(text); }, [text]);
  return (
    <div className="border border-zinc-300 bg-white">
      <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-100 border-b border-zinc-300">
        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">{title}</span>
        <CopyBtn text={val} tid={`copy-${tid}`} />
      </div>
      <textarea data-testid={tid} value={val} onChange={(e) => setVal(e.target.value)} rows={Math.min(8, Math.max(3, Math.ceil((val || "").length / 50)))}
        className="w-full p-3 text-sm resize-none focus:outline-none font-body leading-relaxed" />
    </div>
  );
}


const CHANNELS = ["Email", "WhatsApp", "Proposal", "LinkedIn"];
const COMPOSE_SUGGESTIONS = [
  "Write a warm first outreach email",
  "Draft a short WhatsApp nudge",
  "Create a full proposal with pricing",
  "Follow up after they went quiet",
];

function ComposeTab({ leadId }) {
  const [channel, setChannel] = useState("Email");
  const [instruction, setInstruction] = useState("");
  const [context, setContext] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = () => api.get("/composer-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t) { setChannel(t.channel || "Email"); setInstruction(t.instruction || ""); }
  };

  const saveTemplate = async () => {
    if (!instruction.trim()) { toast.error("Type an instruction first"); return; }
    const name = window.prompt("Name this prompt template:", instruction.slice(0, 40));
    if (!name) return;
    try { await api.post("/composer-templates", { name, channel, instruction }); toast.success("Template saved"); loadTemplates(); }
    catch (e) { toast.error(errText(e)); }
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/composer-templates/${id}`); loadTemplates(); toast.success("Template deleted"); }
    catch (e) { toast.error(errText(e)); }
  };

  const gen = async () => {
    if (!instruction.trim()) { toast.error("Tell the composer what you want to write"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/compose`, { channel, instruction, context });
      setOut(data.text);
      toast.success(`${channel} draft ready`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const copy = () => {
    safeCopy(out).then((ok) => {
      if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1200); toast.success("Copied to clipboard"); }
      else toast.error("Couldn't copy — select the text manually");
    });
  };

  return (
    <div className="space-y-4" data-testid="compose-tab">
      <p className="text-xs text-zinc-500">Type what you want, pick a channel, and generate a draft using this lead's full context + your company resources.</p>

      {/* Saved prompt templates */}
      <div className="flex items-center gap-2">
        <select data-testid="composer-template-select" onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }} defaultValue=""
          className="flex-1 border border-zinc-300 rounded-lg text-xs px-2 py-2 focus:border-klein focus:outline-none">
          <option value="">Load a saved prompt…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.channel}</option>)}
        </select>
        <button data-testid="save-template-btn" onClick={saveTemplate}
          className="flex items-center gap-1.5 border border-zinc-300 rounded-lg text-xs font-medium px-3 py-2 hover:border-klein hover:text-klein transition-colors">
          <Save className="h-3.5 w-3.5" /> Save prompt
        </button>
      </div>
      {templates.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {templates.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 text-[11px] bg-zinc-100 rounded-full pl-2.5 pr-1 py-1 text-zinc-600">
              {t.name}
              <button data-testid={`delete-template-${t.id}`} onClick={() => deleteTemplate(t.id)} className="hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Channel</label>
        <div className="grid grid-cols-4 gap-1.5">
          {CHANNELS.map((c) => (
            <button key={c} data-testid={`compose-channel-${c.toLowerCase()}`} onClick={() => setChannel(c)}
              className={`text-xs font-bold uppercase tracking-wide py-2 rounded-lg border transition-colors ${channel === c ? "bg-klein text-white border-klein" : "bg-white border-zinc-300 hover:border-klein"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">What do you want to write?</label>
        <textarea data-testid="compose-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2}
          placeholder="e.g. Write a warm follow-up asking to hop on a quick call this week"
          className="w-full border border-zinc-300 rounded-lg p-2.5 text-sm resize-none focus:border-klein focus:ring-2 focus:ring-klein/20 focus:outline-none" />
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {COMPOSE_SUGGESTIONS.map((s) => (
            <button key={s} data-testid={`compose-suggest`} onClick={() => setInstruction(s)}
              className="text-[11px] border border-zinc-300 rounded-full px-2.5 py-1 text-zinc-600 hover:border-klein hover:text-klein transition-colors">
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Extra context (optional)</label>
        <textarea data-testid="compose-context" value={context} onChange={(e) => setContext(e.target.value)} rows={2}
          placeholder="e.g. We met at the founders meetup. They mentioned raising a seed round."
          className="w-full border border-zinc-300 rounded-lg p-2.5 text-sm resize-none focus:border-klein focus:ring-2 focus:ring-klein/20 focus:outline-none" />
      </div>

      <button data-testid="compose-generate" onClick={gen} disabled={busy}
        className="w-full bg-klein hover:bg-klein-hover text-white text-sm font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
        {out ? "Regenerate" : "Generate draft"}
      </button>

      {out && (
        <div className="border border-zinc-300 rounded-lg bg-white" data-testid="compose-output">
          <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-100 border-b border-zinc-300 rounded-t-lg">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">{channel} draft · editable</span>
            <button data-testid="compose-copy" onClick={copy} className="flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-klein transition-colors">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} Copy
            </button>
          </div>
          <textarea data-testid="compose-text" value={out} onChange={(e) => setOut(e.target.value)} rows={Math.min(20, Math.max(6, Math.ceil(out.length / 48)))}
            className="w-full p-3 text-sm resize-none focus:outline-none font-body leading-relaxed rounded-b-lg" />
        </div>
      )}
    </div>
  );
}


const PRIORITY_CLS = {
  "Very High": "bg-red-600 text-white border-red-700",
  "High": "bg-klein text-white border-klein",
  "Medium": "bg-zinc-200 text-zinc-900 border-zinc-400",
  "Low": "bg-zinc-100 text-zinc-500 border-zinc-300",
};

function Metric({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 px-3 py-2.5 border-b border-zinc-200 last:border-0">
      <Icon className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
      <div className="w-32 shrink-0 text-[11px] font-bold uppercase tracking-wide text-zinc-500 pt-0.5">{label}</div>
      <div className="flex-1 text-sm">{children}</div>
    </div>
  );
}

function briefToLines(brief, note, leadName) {
  const L = [];
  L.push(`STARTUP TIMES — LEAD BRIEF`);
  L.push(leadName || "");
  L.push("");
  L.push("ACTION DASHBOARD");
  L.push(`ICP Score:          ${brief.icp_score}/100 (${brief.tier})`);
  L.push(`Decision:           ${brief.decision}`);
  L.push(`Buying Probability: ${brief.buying_probability}%`);
  L.push(`Priority:           ${brief.priority}`);
  L.push(`Best Angle:         ${brief.best_angle}`);
  L.push(`Best Contact:       ${brief.best_contact_method}`);
  L.push(`Next Action:        ${brief.next_action}`);
  L.push("");
  if (brief.summary) { L.push("SUMMARY"); L.push(brief.summary); L.push(""); }
  if (brief.highlights?.length) { L.push("WHY HIGH-VALUE"); brief.highlights.forEach((h) => L.push(`• ${h}`)); L.push(""); }
  if (brief.recommendation) { L.push("RECOMMENDATION"); L.push(brief.recommendation); L.push(""); }
  if (brief.opening_lines?.length) { L.push("OPENING LINES"); brief.opening_lines.forEach((o, i) => L.push(`${i + 1}. ${o}`)); L.push(""); }
  if (brief.likely_objections?.length) {
    L.push("LIKELY OBJECTIONS");
    brief.likely_objections.forEach((o) => L.push(`- ${o.objection}: ${o.response}`));
    L.push("");
  }
  L.push("LINKEDIN CONNECTION NOTE");
  L.push(note || brief.connection_note || "");
  return L.join("\n");
}

function briefToHtml(brief, note, leadName) {
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const li = (arr, fn) => (arr || []).map(fn).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(leadName)} — Lead Brief</title>
<style>
  *{box-sizing:border-box} body{font-family:'IBM Plex Sans',Arial,sans-serif;color:#09090B;margin:0;padding:32px;max-width:820px}
  h1{font-size:26px;margin:0} .sub{color:#52525B;font-family:monospace;margin:2px 0 18px}
  table{width:100%;border-collapse:collapse;border:2px solid #000;margin-bottom:20px}
  td{padding:8px 10px;border-bottom:1px solid #e4e4e7;font-size:14px} td.k{width:190px;font-weight:700;text-transform:uppercase;font-size:11px;letter-spacing:.06em;color:#52525B}
  .na{background:#FFD700;padding:2px 6px;font-weight:600}
  h2{font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:#52525B;margin:18px 0 6px}
  li{margin:4px 0;font-size:14px} .rec{border-left:3px solid #002FA7;background:#eff4ff;padding:10px 12px;font-size:14px}
  .note{border:1px solid #d4d4d8;padding:12px;font-size:14px;white-space:pre-wrap} .obj b{color:#b91c1c}
</style></head><body>
  <h1>${esc(leadName)}</h1><div class="sub">Startup Times · Lead Brief</div>
  <table>
    <tr><td class="k">ICP Score</td><td>${brief.icp_score}/100 · ${esc(brief.tier)}</td></tr>
    <tr><td class="k">Decision</td><td>${esc(brief.decision)}</td></tr>
    <tr><td class="k">Buying Probability</td><td>${brief.buying_probability}%</td></tr>
    <tr><td class="k">Priority</td><td>${esc(brief.priority)}</td></tr>
    <tr><td class="k">Best Angle</td><td>${esc(brief.best_angle)}</td></tr>
    <tr><td class="k">Best Contact</td><td>${esc(brief.best_contact_method)}</td></tr>
    <tr><td class="k">Next Action</td><td><span class="na">${esc(brief.next_action)}</span></td></tr>
  </table>
  ${brief.summary ? `<h2>Summary</h2><p>${esc(brief.summary)}</p>` : ""}
  ${brief.highlights?.length ? `<h2>Why high-value</h2><ul>${li(brief.highlights, (h) => `<li>${esc(h)}</li>`)}</ul>` : ""}
  ${brief.recommendation ? `<h2>Recommendation</h2><div class="rec">${esc(brief.recommendation)}</div>` : ""}
  ${brief.opening_lines?.length ? `<h2>Opening lines</h2><ol>${li(brief.opening_lines, (o) => `<li>${esc(o)}</li>`)}</ol>` : ""}
  ${brief.likely_objections?.length ? `<h2>Likely objections</h2><ul class="obj">${li(brief.likely_objections, (o) => `<li><b>${esc(o.objection)}:</b> ${esc(o.response)}</li>`)}</ul>` : ""}
  <h2>LinkedIn connection note</h2><div class="note">${esc(note || brief.connection_note)}</div>
</body></html>`;
}

function BriefView({ brief, leadId, leadName, senderDefault }) {
  const [note, setNote] = useState(brief.connection_note || "");
  const [senderName, setSenderName] = useState(senderDefault || "");
  const [senderTitle, setSenderTitle] = useState("Founder of Startup Times");
  const [regenBusy, setRegenBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setNote(brief.connection_note || ""); }, [brief]);

  const copyBrief = () => {
    safeCopy(briefToLines(brief, note, leadName));
    setCopied(true); setTimeout(() => setCopied(false), 1400);
    toast.success("Brief copied to clipboard");
  };

  const downloadBrief = () => {
    const blob = new Blob([briefToHtml(brief, note, leadName)], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(leadName || "lead").replace(/\s+/g, "-").toLowerCase()}-brief.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("One-page brief downloaded");
  };

  const regenNote = async () => {
    setRegenBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/connection-note`, { sender_name: senderName, sender_title: senderTitle });
      setNote(data.connection_note);
      toast.success("Note rewritten in your voice");
    } catch (e) { toast.error(errText(e)); } finally { setRegenBusy(false); }
  };

  return (
    <div className="space-y-5 rise">
      {/* Export bar */}
      <div className="flex gap-2">
        <button data-testid="brief-copy" onClick={copyBrief}
          className="flex-1 flex items-center justify-center gap-1.5 border border-zinc-400 bg-white hover:bg-zinc-100 text-xs font-bold uppercase tracking-wide py-2 transition-colors">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} Copy brief
        </button>
        <button data-testid="brief-download" onClick={downloadBrief}
          className="flex-1 flex items-center justify-center gap-1.5 bg-[#09090B] text-white hover:bg-zinc-800 text-xs font-bold uppercase tracking-wide py-2 transition-colors">
          <Download className="h-3.5 w-3.5" /> Download 1-pager
        </button>
      </div>

      {/* Action Dashboard */}
      <div className="border-2 border-black bg-white">
        <div className="bg-[#09090B] text-white px-3 py-2 flex items-center gap-2">
          <Gauge className="h-4 w-4 text-gold" />
          <span className="font-head font-bold text-sm tracking-tight">ACTION DASHBOARD</span>
        </div>
        <Metric icon={Target} label="ICP Score">
          <span className="font-mono font-bold">{brief.icp_score}/100</span>
          <span className="text-zinc-400"> · {brief.tier}</span>
        </Metric>
        <Metric icon={CheckCircle2} label="Decision">
          <span className="font-semibold">{brief.decision}</span>
        </Metric>
        <Metric icon={Gauge} label="Buying Probability">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-zinc-200 max-w-[160px]"><div className="h-2 bg-emerald-500" style={{ width: `${brief.buying_probability}%` }} /></div>
            <span className="font-mono font-bold">{brief.buying_probability}%</span>
          </div>
        </Metric>
        <Metric icon={Sparkles} label="Priority">
          <span className={`inline-block border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${PRIORITY_CLS[brief.priority] || PRIORITY_CLS.Low}`}>{brief.priority}</span>
        </Metric>
        <Metric icon={Compass} label="Best Angle">
          <span className="font-medium">{brief.best_angle}</span>
        </Metric>
        <Metric icon={MessageCircle} label="Best Contact">
          {brief.best_contact_method}
        </Metric>
        <Metric icon={ChevronRight} label="Next Action">
          <span className="bg-gold px-2 py-0.5 font-medium border border-black/10">{brief.next_action}</span>
        </Metric>
      </div>

      {/* Summary */}
      {brief.summary && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Summary</div>
          <p className="text-sm leading-relaxed text-zinc-800">{brief.summary}</p>
        </div>
      )}

      {/* Highlights */}
      {brief.highlights?.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Why they're a high-value lead</div>
          <ul className="space-y-1.5">
            {brief.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendation */}
      {brief.recommendation && (
        <div className="border-l-2 border-klein bg-blue-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-klein mb-1 flex items-center gap-1.5"><ThumbsUp className="h-3.5 w-3.5" /> Recommendation</div>
          <p className="text-sm leading-relaxed">{brief.recommendation}</p>
        </div>
      )}

      {/* Talking points — opening lines */}
      {brief.opening_lines?.length > 0 && (
        <div data-testid="brief-openers">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Quote className="h-3.5 w-3.5" /> Opening lines</div>
          <div className="space-y-1.5">
            {brief.opening_lines.map((o, i) => (
              <div key={i} className="flex items-start justify-between gap-2 border border-zinc-300 bg-white px-3 py-2">
                <span className="text-sm">{o}</span>
                <CopyBtn text={o} tid={`copy-opener-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Talking points — likely objections */}
      {brief.likely_objections?.length > 0 && (
        <div data-testid="brief-objections">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" /> Likely objections & how to respond</div>
          <div className="space-y-1.5">
            {brief.likely_objections.map((o, i) => (
              <div key={i} className="border border-zinc-300 bg-white px-3 py-2">
                <div className="text-xs font-bold text-red-700">{o.objection}</div>
                <div className="text-sm text-zinc-700">{o.response}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connection note + sender voice */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">LinkedIn connection note</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input data-testid="sender-name" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Your name"
            className="border border-zinc-400 text-xs px-2 py-1.5 focus:border-klein focus:outline-none" />
          <input data-testid="sender-title" value={senderTitle} onChange={(e) => setSenderTitle(e.target.value)} placeholder="Your title"
            className="border border-zinc-400 text-xs px-2 py-1.5 focus:border-klein focus:outline-none" />
        </div>
        <button data-testid="regen-note" onClick={regenNote} disabled={regenBusy}
          className="mb-2 w-full flex items-center justify-center gap-2 bg-klein hover:bg-klein-hover text-white text-xs font-medium py-2 transition-colors disabled:opacity-60">
          {regenBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Rewrite in this voice
        </button>
        <MsgBlock title={`From ${senderName || "you"}`} text={note} tid="brief-connection-note" />
      </div>
    </div>
  );
}


const PROFILE_FIELDS = [
  ["name", "Name", "text"], ["company", "Company", "text"],
  ["self_described_title", "Self-described title", "text"], ["formal_title", "Formal title", "text"],
  ["headline", "Headline", "text"], ["follower_count", "Followers", "number"],
  ["location", "Location", "text"], ["experience_years", "Experience (yrs)", "number"],
  ["current_role_start_year", "Role start year", "number"], ["industry", "Industry", "text"],
  ["education", "Education", "text"], ["linkedin_url", "LinkedIn URL", "text"],
  ["website", "Website", "text"],
  ["phone", "Phone", "text"], ["email", "Email", "text"],
];

function ProfileTab({ lead, onSaved, canEdit }) {
  const [form, setForm] = useState(lead);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(lead); }, [lead]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = PROFILE_FIELDS.some(([k]) => String(form[k] ?? "") !== String(lead[k] ?? "")) ||
    !!form.verification_badge !== !!lead.verification_badge || !!form.open_to_work !== !!lead.open_to_work;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      PROFILE_FIELDS.forEach(([k, , t]) => { payload[k] = t === "number" ? (Number(form[k]) || 0) : form[k]; });
      payload.verification_badge = !!form.verification_badge;
      payload.open_to_work = !!form.open_to_work;
      const { data } = await api.patch(`/leads/${lead.id}`, payload);
      onSaved(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-tab">
      <div className="grid grid-cols-2 gap-3">
        {PROFILE_FIELDS.map(([k, label, type]) => (
          <div key={k} className={["headline", "linkedin_url"].includes(k) ? "col-span-2" : ""}>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5 block">{label}</label>
            <input data-testid={`profile-${k}`} type={type} value={form[k] ?? ""} disabled={!canEdit}
              onChange={(e) => set(k, e.target.value)}
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm focus:border-klein focus:outline-none disabled:bg-zinc-50" />
          </div>
        ))}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={!canEdit} checked={!!form.verification_badge} onChange={(e) => set("verification_badge", e.target.checked)} className="accent-klein" /> Verified badge</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={!canEdit} checked={!!form.open_to_work} onChange={(e) => set("open_to_work", e.target.checked)} className="accent-klein" /> Open to work</label>
      </div>

      {canEdit && dirty && (
        <button data-testid="profile-save" onClick={save} disabled={saving}
          className="mt-4 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 text-sm font-bold transition-colors hard-shadow-sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
        </button>
      )}

      <div className="mt-5 border-t border-zinc-200 pt-3 grid grid-cols-2 gap-y-1 text-xs font-mono text-zinc-500">
        <div>Source</div><div className="text-zinc-800">{lead.source || "—"}</div>
        <div>Assigned</div><div className="text-zinc-800">{lead.assigned_to_name || "Unassigned"}</div>
        <div>Created</div><div className="text-zinc-800">{lead.created_date?.slice(0, 10)}</div>
        <div>Last contact</div><div className="text-zinc-800">{lead.last_contact_date?.slice(0, 10) || "—"}</div>
      </div>
    </div>
  );
}

const ACT_ICON = {
  note: StickyNote, stage: ChevronRight, objection: ShieldAlert, assign: UserCheck,
  won: Trophy, file: Paperclip, created: Sparkles, ghost: AlertTriangle,
};

function NotesTab({ leadId }) {
  const [acts, setActs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/leads/${leadId}/activity`).then((r) => setActs(r.data));
  useEffect(() => { load(); }, [leadId]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/leads/${leadId}/notes`, { text }); setText(""); load(); toast.success("Note added"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="notes-tab">
      <div className="flex gap-2 mb-4">
        <textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Log a call, meeting, or note…"
          className="flex-1 border border-zinc-400 p-2 text-sm resize-none focus:border-klein focus:outline-none" />
        <button data-testid="note-add" onClick={add} disabled={busy || !text.trim()}
          className="bg-klein hover:bg-klein-hover text-white px-3 self-stretch flex items-center transition-colors disabled:opacity-50">
          <Send className="h-4 w-4" />
        </button>
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Activity timeline</div>
      <div className="space-y-3">
        {acts.map((a) => {
          const Icon = ACT_ICON[a.type] || StickyNote;
          return (
            <div key={a.id} className="flex gap-3" data-testid={`activity-${a.type}`}>
              <div className="mt-0.5"><div className="h-6 w-6 bg-zinc-900 text-white grid place-items-center"><Icon className="h-3.5 w-3.5" /></div></div>
              <div className="flex-1 border-b border-zinc-100 pb-3">
                <div className={`text-sm ${a.type === "note" ? "" : "text-zinc-600"}`}>{a.text}</div>
                <div className="text-[10px] font-mono text-zinc-400 mt-0.5">{a.author_name} · {new Date(a.created_date).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
        {acts.length === 0 && <div className="text-xs text-zinc-400 font-mono py-6 text-center">No activity yet. Add the first note.</div>}
      </div>
    </div>
  );
}

function FilesTab({ leadId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const load = () => api.get(`/leads/${leadId}/attachments`).then((r) => setFiles(r.data));
  useEffect(() => { load(); }, [leadId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/leads/${leadId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File attached"); load(); }
    catch (e2) { toast.error(errText(e2)); } finally { setBusy(false); e.target.value = ""; }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/attachments/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const del = async (f) => {
    try { await api.delete(`/attachments/${f.id}`); load(); toast.success("Deleted"); } catch { toast.error("Delete failed"); }
  };

  const fmt = (b) => b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e3).toFixed(0)} KB`;

  return (
    <div data-testid="files-tab">
      <input ref={ref} type="file" onChange={upload} className="hidden" data-testid="file-input" />
      <button data-testid="file-upload-btn" onClick={() => ref.current?.click()} disabled={busy}
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-zinc-400 hover:border-klein py-4 text-sm font-medium transition-colors mb-4">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Attach a file (docs, images, badges — max 15MB)
      </button>
      <div className="space-y-2">
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 border border-zinc-300 bg-white px-3 py-2" data-testid={`file-${f.id}`}>
            <FileText className="h-4 w-4 text-zinc-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{f.filename}</div>
              <div className="text-[10px] font-mono text-zinc-400">{fmt(f.size)} · {f.uploaded_by_name} · {f.uploaded_at?.slice(0, 10)}</div>
            </div>
            <button onClick={() => download(f)} className="text-zinc-500 hover:text-klein" data-testid={`file-dl-${f.id}`}><Download className="h-4 w-4" /></button>
            <button onClick={() => del(f)} className="text-zinc-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {files.length === 0 && <div className="text-xs text-zinc-400 font-mono py-6 text-center">No files attached yet.</div>}
      </div>
    </div>
  );
}